<template>
  <div
    v-if="!isSlotsEmpty"
    v-observe-visibility="{
      callback: visibilityChanged,
      throttle: 1000,
      intersection: {
        threshold: 0.75,
      },
    }"
    class="carousel"
    data-test-carousel
  >
    <button
      type="button"
      :class="['carousel__btn', 'carousel__btn--prev', { 'carousel__btn--disabled': !isPreviousEnabled }]"
      data-test-previous-btn
      @click="previousElement"
    >
      <span class="adn-icon adn-icon--navigation-direction-left carousel__btn-icon" aria-label="Précédent" />
    </button>
    <button
      type="button"
      :class="['carousel__btn', 'carousel__btn--next', { 'carousel__btn--disabled': !isNextEnabled }]"
      data-test-next-btn
      @click="nextElement"
    >
      <span class="adn-icon adn-icon--navigation-direction-right carousel__btn-icon" aria-label="Suivant" />
    </button>
    <div ref="carouselHolder" class="carousel__holder">
      <div ref="carouselList" class="carousel__group-list row" :style="translateTransformation">
        <ul
          v-for="(groupIndex, index) in groupsOfElementsIndices"
          :key="groupIndex"
          :class="['carousel__list', 'row', { 'carousel__list--current': index == currentIndex }]"
          data-test-group
        >
          <li
            v-for="(elementIndex, position) in elementsIndices(groupIndex)"
            :key="`${groupIndex}-${position}`"
            :class="['carousel__item', { 'carousel__item--filler': elementIndex === -1 }]"
            data-test-group-item
          >
            <div v-if="elementIndex === -1">
              <slot :name="0"></slot>
            </div>
            <slot v-else :name="elementIndex"></slot>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { ObserveVisibility } from 'vue3-observe-visibility'

export default {
  name: 'PCarousel',
  directives: {
    ObserveVisibility,
  },
  props: {
    maxNbVisibleElements: {
      type: Number,
    },
  },
  data() {
    return {
      currentIndex: 0,
      moveSteps: 1,
    }
  },
  computed: {
    groupsOfElementsIndices() {
      const nbGroupsOfElements = Math.ceil(this.allItemIndices.length / this.maxNbVisibleElements)
      return [...Array(nbGroupsOfElements).keys()]
    },
    allItemIndices() {
      return Object.keys(this.$slots)
    },
    isSlotsEmpty() {
      return this.allItemIndices.length === 0
    },
    isNextEnabled() {
      return this.currentIndex < this.groupsOfElementsIndices.length - 1
    },
    isPreviousEnabled() {
      return this.currentIndex > 0
    },
    translateTransformation() {
      return `transform: translateX(${-this.currentIndex * 100}%)`
    },
  },
  methods: {
    visibilityChanged(isVisible) {
      if (isVisible) this.sendCurrentVisibleElements()
    },
    nextElement() {
      if (!this.isNextEnabled) return
      this.currentIndex += this.moveSteps
      this.sendCurrentVisibleElements()
    },
    previousElement() {
      if (!this.isPreviousEnabled) return
      this.currentIndex -= this.moveSteps
      this.sendCurrentVisibleElements()
    },
    sendCurrentVisibleElements() {
      const payload = {
        productsIndices: this.visibleElementsIndices(this.currentIndex),
      }
      this.$emit('carouselDisplay', payload)
    },
    visibleElementsIndices(groupIndex) {
      return this.allItemIndices.slice(
        groupIndex * this.maxNbVisibleElements,
        (groupIndex + 1) * this.maxNbVisibleElements,
      )
    },
    elementsIndices(groupIndex) {
      const indicesSlice = this.visibleElementsIndices(groupIndex)
      return indicesSlice.concat(Array(this.maxNbVisibleElements - indicesSlice.length).fill(-1))
    },
  },
}
</script>

<style scoped lang="scss">
$product-margin: 2rem;
$carousel-transition-time: 0.25s;

.carousel {
  position: relative;
  display: flex;
  overflow: visible;
}

.carousel__btn {
  @media (max-width: $breakpoint-xl) {
    display: none;
  }

  position: absolute;
  width: 40px;
  height: 40px;
  cursor: pointer;
  align-self: center;
  transition: transform 0.02s;
  z-index: 15;
  border: none;
  background: none;
  padding: 0;
}

.carousel__btn--prev {
  left: 6px;

  &:hover {
    transform: scale(1.2);
  }
}

.carousel__btn--next {
  right: 6px;

  &:hover {
    transform: scale(1.2);
  }
}

.carousel__btn-icon {
  display: block;
  font-size: 4rem;
  line-height: 4rem;
}

.carousel__btn--disabled {
  visibility: hidden;
}

.carousel__holder {
  overflow: hidden;

  @media (min-width: $breakpoint-xl) {
    width: 100%;
  }
}

.carousel__list {
  display: flex;
  flex-wrap: nowrap;
  flex-shrink: 0;
  padding: 1rem;
  list-style: none;
  width: auto;

  @media (min-width: $breakpoint-xl) {
    width: calc(100% - 30px);
    margin: 0 15px;
    padding: 5px 0 30px;
    justify-content: center;

    & .carousel__item {
      transition: visibility 0s linear $carousel-transition-time;
      visibility: hidden;
    }

    &.carousel__list--current .carousel__item {
      transition: visibility 0s linear 0s;
      visibility: visible;
    }
  }
}

.carousel__item {
  margin: 0 10px;

  @media (min-width: $breakpoint-xl) {
    padding: 0;
    margin: 0;

    &:not(:last-child) {
      margin: 0 30px 0 0;
    }
  }

  &.carousel__item--filler {
    visibility: hidden !important;
  }
}

.carousel__group-list {
  display: flex;
  flex-wrap: nowrap;
  align-content: space-between;
  overflow-x: scroll;
  padding: 0 0 0 5px;
  list-style: none;
  max-width: calc(100% + 140px);

  @media (min-width: $breakpoint-xl) {
    left: 0;
    transition: transform $carousel-transition-time ease-out;
    position: relative;
    overflow-x: visible;
    padding: 0;
    margin: 0;
    width: 100%;
  }
}

.carousel__group-item {
  padding: 0 0 0 10px;

  @media (min-width: $breakpoint-xl) {
    padding: 0 calc($product-margin / 2);
  }
}

ul {
  margin: 0;
  padding: 0;
}
</style>
